class MobileDock extends HTMLElement {
  constructor() {
    super();

    this.bar = null;
    this.count = null;
    this.source = null;
    this.observer = null;
    this.account = null;
    this.accountCell = null;
    this.searchCell = null;
    this.barObserver = null;
    this.barHeight = 0;
    this.lastHeight = null;
    this.lastScrollTop = 0;
    this.rafScroll = 0;
    this.rafViewport = 0;

    this._onScroll = this._onScroll.bind(this);
    this._onViewportChange = this._onViewportChange.bind(this);
    this._renderCount = this._renderCount.bind(this);
    this._onAccountClick = this._onAccountClick.bind(this);
    this._onSearchClick = this._onSearchClick.bind(this);
    this._onSearchModalOpen = this._onSearchModalOpen.bind(this);
  }

  connectedCallback() {
    this.bar = this.querySelector('.mobile-dock__bar');
    if (!this.bar) return;

    if (this.dataset.sticky === 'on_scroll') {
      this.lastScrollTop = Math.max(window.scrollY, 0);
      window.addEventListener('scroll', this._onScroll, { passive: true });
    }

    this._initCount();
    this._initAccount();
    this._initSearch();
    this._initHeight();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onViewportChange);
    window.visualViewport?.removeEventListener('resize', this._onViewportChange);
    window.visualViewport?.removeEventListener('scroll', this._onViewportChange);
    this.observer?.disconnect();
    this.accountCell?.removeEventListener('click', this._onAccountClick);
    this.searchCell?.removeEventListener('click', this._onSearchClick);
    document.removeEventListener('searchmodal:open', this._onSearchModalOpen);
    this.barObserver?.disconnect();
    document.body.style.removeProperty('--mobile-dock-height');
    if (this.rafScroll) cancelAnimationFrame(this.rafScroll);
    if (this.rafViewport) cancelAnimationFrame(this.rafViewport);
  }

  _initHeight() {
    if (this.dataset.sticky === 'disable') return;

    this.barObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      this.barHeight = Math.round(entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height);
      this._renderHeight();
    });

    this.barObserver.observe(this.bar);

    if (this.dataset.sticky !== 'always') return;

    window.addEventListener('resize', this._onViewportChange, { passive: true });
    window.visualViewport?.addEventListener('resize', this._onViewportChange, { passive: true });
    window.visualViewport?.addEventListener('scroll', this._onViewportChange, { passive: true });
  }

  _onViewportChange() {
    if (this.rafViewport) return;

    this.rafViewport = requestAnimationFrame(() => {
      this.rafViewport = 0;
      this._renderHeight();
    });
  }

  _renderHeight() {
    const occupied =
      this.dataset.sticky === 'always' || this.bar.classList.contains('mobile-dock__bar--revealed');

    let height = 0;

    if (occupied && this.bar.getClientRects().length) {
      const rect = this.bar.getBoundingClientRect();
      const gap = Math.round(document.documentElement.clientHeight - rect.bottom);

      height = Math.round(rect.height) + Math.max(0, gap);
    }

    if (height === this.lastHeight) return;
    this.lastHeight = height;

    document.body.style.setProperty('--mobile-dock-height', `${height}px`);
  }

  _initAccount() {
    this.account = this.querySelector('shopify-account');
    if (!this.account) return;

    this.accountCell = this.account.closest('.mobile-dock__item');
    this.accountCell?.addEventListener('click', this._onAccountClick);
  }

  _onAccountClick(event) {
    if (event.target.closest('shopify-account')) return;

    this.account.shadowRoot?.querySelector('button')?.click();
  }

  _initSearch() {
    this.searchCell = this.querySelector('.mobile-dock__item--search');
    if (!this.searchCell) return;

    this.searchCell.addEventListener('click', this._onSearchClick);
    document.addEventListener('searchmodal:open', this._onSearchModalOpen);
  }

  _onSearchModalOpen() {
    const header = document.querySelector('.header');
    if (header && header.getBoundingClientRect().bottom > 0) return;

    const bottoms = Array.from(document.querySelectorAll('.shopify-section-group-header-group'))
      .map((el) => el.getBoundingClientRect().bottom)
      .filter((bottom) => bottom > 0);

    const offset = bottoms.length ? Math.round(Math.max(...bottoms)) : 0;

    document.documentElement.style.setProperty('--header-offset-height', `${offset}px`);
  }

  _onSearchClick(event) {
    const visible = (selector) =>
      Array.from(document.querySelectorAll(selector)).find((el) => el.getClientRects().length);

    const trigger = visible('.header .search-link') || visible('.header .search-field__text');

    if (trigger) {
      event.preventDefault();

      if (trigger.tagName === 'INPUT') {
        const stickyType = trigger.closest('.header')?.dataset.stickyType;
        const stickyHeader = document.querySelector('sticky-header');

        if (stickyType === 'on_scroll') {
          stickyHeader?.reveal?.();
          stickyHeader?.disableTransitionForMs?.(300);
        } else if (stickyType !== 'always') {
          window.scrollTo({ top: 0, behavior: 'instant' });
          stickyHeader?.reset?.();
        }

        trigger.focus();
      } else {
        trigger.click();
      }

      return;
    }

    const modal = Array.from(document.querySelectorAll('.header search-modal')).find(
      (el) => el.summaryToggle && !el.querySelector('.header-search-style-field')
    );
    if (!modal) return;

    event.preventDefault();
    modal.open({ target: this.searchCell }, true);
  }

  _onScroll() {
    if (this.rafScroll) return;

    this.rafScroll = requestAnimationFrame(() => {
      this.rafScroll = 0;

      const scrollTop = Math.max(window.scrollY, 0);
      const delta = scrollTop - this.lastScrollTop;

      if (Math.abs(delta) < 5) return;

      this.bar.classList.toggle('mobile-dock__bar--revealed', delta < 0 && scrollTop > 0);
      this._renderHeight();
      this.lastScrollTop = scrollTop;
    });
  }

  _initCount() {
    this.count = this.querySelector('.mobile-dock__count');
    if (!this.count) return;

    this.source = document.querySelector('.header [id^="cart-icon-bubble-"]');
    if (!this.source) return;

    this.observer = new MutationObserver(this._renderCount);
    this.observer.observe(this.source, { childList: true, subtree: true, characterData: true });
  }

  _renderCount() {
    const value = this.source.querySelector('.cart-icon-bubble-count')?.textContent.trim() || '0';

    this.count.textContent = value;
    this.count.hidden = value === '0';
  }
}

if (!customElements.get('mobile-dock')) customElements.define('mobile-dock', MobileDock);
